import {
  Component,
  EventEmitter,
  OnInit,
  viewChild,
  inject,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { CONTACT_DETAIL_CONTAINER } from 'src/app/constants/common.constants';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { Contact } from 'src/app/model/contacts/contact';
import { ContactApiService } from '../../../services/api/contact-api.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { ContactBuilderService } from '../../../services/builder/contact-builder.service';
import { ContactTableService } from 'src/app/services/table/contact-table.service';
import { getContactTableColumns } from '../../definitions/contacts-table-definition';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { BreadCrumb } from 'src/app/model/common/bread-crumb';
import { BusinessUnitFilterComponent } from 'src/app/core/components/business-unit-filter/business-unit-filter.component';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { BusinessUnitFilterComponent as BusinessUnitFilterComponent_1 } from '../../../core/components/business-unit-filter/business-unit-filter.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { Panel } from '@openng/optimus-ui/panel';
import { ContactMobileFilterComponent } from '../../components/contact-mobile-filter/contact-mobile-filter.component';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { AdminApiService } from 'src/app/services/api/admin-api.service';
import { isNullOrUndefined } from 'src/app/utils/utils';

/**
 * Main search component for the contact module.
 */
@Component({
  selector: 'app-contacts-search-container',
  templateUrl: './contacts-search-container.component.html',
  styleUrls: ['./contacts-search-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    BusinessUnitFilterComponent_1,
    ButtonDirective,
    Panel,
    ContactMobileFilterComponent,
    ProgressSpinnerComponent,
    GeneralGridComponent,
    TranslatePipe,
  ],
})
export class ContactsSearchContainerComponent
  extends SearchContainerComponent<Contact>
  implements OnInit, OnDestroy
{
  protected _translateService = inject(TranslateService);
  protected _breadCrumbBuilderService = inject(BreadCrumbBuilderService);
  protected _contactApi = inject(ContactApiService);
  protected _contactBuilder = inject(ContactBuilderService);
  protected _contactTableService = inject(ContactTableService);
  protected _authApi = inject(AuthApiService);
  protected _tabService = inject(TabService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _adminApi = inject(AdminApiService);

  readonly businessUnitFilter =
    viewChild<BusinessUnitFilterComponent>('buFilter');
  allContacts: any[] = [];
  contactsLoading: boolean = true;
  availableCompanies: any[] = [];
  contactsLoadingEvent = new EventEmitter<any>();
  scrollHeight = 'calc(100vh - 256px)';
  mobileFilterCollapsed = true;
  firstLoad = 1;

  activeTabChangedSub?: Subscription;

  constructor() {
    super();
  }

  ngOnInit() {
    this.setTableService(this._contactTableService);
    this.setupActiveTabChangedSubscription();
    this.initialize();
  }

  ngOnDestroy() {
    this.activeTabChangedSub?.unsubscribe();
  }

  initialize() {
    if (this._deviceService.isMobile()) {
      this.scrollHeight = 'calc(100vh - 180px)';
    }
    // setup the bread crumb.
    this._translateService.get('CONTACT.SCREEN.CONTACT').subscribe((label) => {
      if (this._deviceService.isMobile()) {
        this._breadCrumbBuilderService.resetAndAddBreadCrumb(
          new BreadCrumb(label, null, false)
        );
      } else {
        this._tabService.updateActiveTabLabel(label);
      }
      this.screenName = label;
    });

    // setup the select item lists and columns for the grid.
    const availableStatus: { label: string; value: string | null }[] = [];
    const activeStatus$ = this._translateService.get('COMMON.LABEL.ACTIVE');
    const inactiveStatus$ = this._translateService.get('COMMON.LABEL.INACTIVE');
    forkJoin([activeStatus$, inactiveStatus$])
      .pipe(take(1))
      .subscribe((messages) => {
        availableStatus.push({ label: '', value: null });
        availableStatus.push({ label: messages[0], value: messages[0] });
        availableStatus.push({ label: messages[1], value: messages[1] });
      });

    this._adminApi.getCompanies().subscribe((companies) => {
      this.availableCompanies = [
        { label: '', value: null },
        ...companies.map((c: { name: string }) => ({
          label: c.name,
          value: c.name,
        })),
      ];
      this.columns = [
        ...getContactTableColumns(
          this.availableCompanies,
          availableStatus,
          this._deviceService.isMobile()
        ),
      ];
      this.applyPreviousFilters();
      this.applyPreviousData();
    });
  }

  /**
   * Method called when a row double click event is fired.  This navigates to the details page.
   * @param $event - Event from the double click event.
   */
  changeViewEvent($event: { data: Contact }) {
    this._contactTableService.setScreenEmbedded(false);
    const tab = this._tabService.buildNewTab(
      CONTACT_DETAIL_CONTAINER,
      true,
      undefined,
      $event.data.id
    );
    if (this._deviceService.isMobile()) {
      this._tabService.setMobileTab(tab);
    } else {
      this._tabService.openTab(tab);
    }
  }

  private queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ) {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'firstName');

    const trueQuery: any = {};
    Object.keys(query).forEach((key) => {
      if (key !== 'isUnavailable' && key !== 'businessUnits') {
        if (query[key]) {
          trueQuery[key] = query[key].trim();
        }
      }
    });
    trueQuery['businessUnits'] = query['businessUnits'];

    this._contactApi
      .queryForContacts(req.pageSize, req.page, trueQuery, sort)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          this.totalRecords = data.totalRecords;
          this._contactTableService.setLastTotalRecords(this.totalRecords);
          this.allContacts = data.items;
          this.elements = [...this.allContacts];
          this.loading = false;
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey: 'CONTACT.MESSAGES.ERROR.LOAD_CONTACTS',
          });
        },
      });
  }

  mobileFilter($event: any) {
    const newLazy: any = {
      filters: {
        ...this.grid()?.table()?.filters,
        fullName: $event.name
          ? {
              value: $event.name,
              matchMode: 'contains',
            }
          : null,
        supervisor: $event.supervisor
          ? {
              value: $event.supervisor,
              matchMode: 'contains',
            }
          : null,
      },
    };
    this.mobileFilterCollapsed = true;
    this._contactTableService.setLastLazyLoad(newLazy);
    this.lazyLoad({ lazy: this._contactTableService.getLastLazyLoad()! });
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    let selectedBusinessUnits = [];
    const businessUnitFilter = this.businessUnitFilter();
    if (businessUnitFilter?.selectedBusinessUnits) {
      // Handle further refreshes & changes to business unit filter component
      selectedBusinessUnits = businessUnitFilter.selectedBusinessUnits;
    } else {
      // Handle initial page load (before the business unit filter subcomponent exists)
      selectedBusinessUnits = this._authApi
        .getUserBusinessUnits()
        .map((bu) => bu.id);
    }
    filters['businessUnits'] = selectedBusinessUnits as unknown as any;

    const query = this._contactBuilder.buildContactQuery(filters);
    this.queryNetwork(req, $event, query);
  }

  handleBusinessUnitChange() {
    this.lazyLoad({ lazy: this._contactTableService.getLastLazyLoad()! });
  }

  canUserAddContact(): boolean {
    return this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTACTS.CONTACTS.CREATE_CONTACT,
    ]);
  }

  addNewUser(): void {
    this._contactTableService.setSelected(null);
    this._contactTableService.setScreenEmbedded(false);
    const tab = this._tabService.buildNewTab(
      CONTACT_DETAIL_CONTAINER,
      true,
      undefined,
      'newContact'
    );
    this._tabService.openTab(tab);
  }

  refresh(): void {
    this.lazyLoad({ lazy: this._contactTableService.getLastLazyLoad()! });
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (!isNullOrUndefined(tab) && tab.header === this.screenName) {
          this.refresh();
        }
      }
    );
  }
}
